import { useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";

function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "could not accept this invite";
}

/**
 * The public screen an invite link lands on. Reads the token from the query
 * string, lets the invitee set a password, and — on success — reloads to
 * `/`, where App's own useAuth check picks up the new session cookie the
 * accept response just set. This is a full navigation, not client-side
 * state, since there is no router in this app; see App.tsx's own pathname
 * check for why that is an acceptable trade-off for two screens.
 */
export function AcceptInvite({ token }: { token: string }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.acceptInvite(token, password);
      window.location.assign("/");
    } catch (err) {
      setError(describeError(err));
      setSubmitting(false);
    }
  };

  return (
    <main className="shell shell--signin">
      <form className="card signin" onSubmit={(event) => void handleSubmit(event)}>
        <h1>Set your password</h1>
        <p className="shell__subtitle">You&rsquo;ll be signed in right after.</p>
        <label className="signin__field">
          <span>New password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            minLength={12}
            autoFocus
          />
        </label>
        <label className="signin__field">
          <span>Confirm password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            required
            minLength={12}
          />
        </label>
        {error ? (
          <p className="card__error" role="alert">
            {error}
          </p>
        ) : null}
        <button type="submit" disabled={submitting}>
          {submitting ? "Setting password…" : "Set password and sign in"}
        </button>
      </form>
    </main>
  );
}
